/**
 * Echo Hunt -- Settings tab: the shared-reshape + bullet-pool dev tool (P7).
 *
 * What this shows, per §16.2 P7's acceptance criterion:
 *  - a pool of 8 bullets built exactly as a real round would build it (P6 fakes + sampled reals, all
 *    reshaped through the same call), with the labels HIDDEN behind a toggle by default. Hiding them by
 *    default is not politeness: §16.0's label-hygiene rule only works if the first thing you see is
 *    what the player sees, and a dev tool that shows `real`/`fake` up front trains the developer to
 *    read the labels instead of the bullets;
 *  - per bullet: the seeded words, the reshaped text, the retention verdict (real bullets only) and the
 *    word counts, so "are the two classes actually symmetric here?" is checkable at a glance;
 *  - every dropped bullet WITH ITS REASON, because a round that came out smaller than requested must be
 *    visible rather than mysterious;
 *  - the fallowness weights, so "why is this bullet never chosen?" is answerable.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Text} from "react-vcomponents";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {InfoButton} from "web-vcore";
import {GetMyEchoProfile} from "../../../EchoHunt/Profiles_Live.js";
import {GetPoolCandidatesWithWeights, BuildBulletPool_Live, IsBulletPoolReady} from "../../../EchoHunt/Reshape_Live.js";
import {BulletPoolResult, HuntSlotView, ToHuntSlotView} from "../../../EchoHunt/Reshape.js";

/** How many bullets the dev pool builds. Matches §16.2 P7's "a pool of 8 bullets". */
const DEV_POOL_SIZE = 8;

export const BulletPoolDevPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const [running, setRunning] = useState(false);
	const [result, setResult] = useState<BulletPoolResult|null>(null);
	const [error, setError] = useState<string|null>(null);
	const [showLabels, setShowLabels] = useState(false);
	const [seedingEnabled, setSeedingEnabled] = useState(true);

	const candidates = GetPoolCandidatesWithWeights();
	const ready = profile != null && IsBulletPoolReady();

	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile to build a bullet pool.</Text>;
	}

	if (error != null) {
		return <Text style={{color: "red"}}>{error}</Text>;
	}

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>Bullet pool (dev preview)</Text>
				<InfoButton ml={5} text={[
					"Builds one round's bullet pool exactly as the real pipeline will (§16.2 P7):",
					" 1. sample real bullets from the corpus, weighted toward unused ones (§4.5), at most one per dream-night;",
					" 2. generate the fakes (P6), one independent call each;",
					" 3. put EVERY bullet -- real and fake -- through the SAME reshape call, with the seeded-word count",
					"    drawn from one shared distribution (§6);",
					" 4. re-check every bullet against the §7 gate, and retention-check the real ones;",
					" 5. shuffle, then assign random slot ids.",
					"",
					"Labels are hidden by default, on purpose: what the player sees is the bullet text, so that is what",
					"you should read first. Use `Show labels` only after you have formed an impression.",
					"",
					"The two classes should be indistinguishable here. If the fakes read differently -- more abstract,",
					"more neatly resolved, more 'seeded' -- the prompt needs work, not the settings.",
				].join("\n")}/>
			</Row>

			<Column mt={6} style={{gap: 2}}>
				<Text style={{opacity: .8}}>{`Corpus: ${candidates.length} usable bullet(s) in ${new Set(candidates.map(a=>a.familyId)).size} dream-night(s)`}</Text>
				{!ready && <Text style={{color: "orange"}}>Not ready -- needs an indexed corpus and a loaded word list.</Text>}
			</Column>

			<Row mt={8} center style={{gap: 8}}>
				<Button text={running ? "Building…" : `Build a pool of ${DEV_POOL_SIZE}`} enabled={ready && !running} onClick={async()=>{
					setRunning(true);
					setResult(null);
					setError(null);
					setShowLabels(false); // always start hidden, even on a rebuild
					try {
						const pool = await BuildBulletPool_Live({N: DEV_POOL_SIZE, X: .65, rng: Math.random, seedingEnabled});
						if (pool == null) setError("The corpus or the word list is not ready (see the note above).");
						else setResult(pool);
					} catch (ex) {
						setError(`${ex}`);
					}
					setRunning(false);
				}}/>
				{running && <ProgressSpinner/>}
				<CheckBox text="Seeded round" value={seedingEnabled} onChange={setSeedingEnabled}/>
				<CheckBox text="Show labels" value={showLabels} onChange={setShowLabels}/>
			</Row>

			{result != null && <PoolResultView result={result} showLabels={showLabels}/>}
		</>
	);
});

const PoolResultView = observer_mgl((props: {result: BulletPoolResult, showLabels: boolean})=>{
	const {result, showLabels} = props;
	// the label-free view model is what the hunt UI will get (§16.0); the labels come from the slots
	// only when the toggle is on, exactly so the hide/show path mirrors the production one
	const views: HuntSlotView[] = ToHuntSlotView(result.slots);

	const realCount = result.slots.filter(a=>a.sourceKind == "real").length;
	const fakeCount = result.slots.filter(a=>a.sourceKind == "fake").length;

	return (
		<Column sel mt={10} style={{gap: 6}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text>{`Slots: ${result.slots.length}`}</Text>
				{showLabels && <Text style={{color: "lightgreen"}}>{`real: ${realCount}`}</Text>}
				{showLabels && <Text style={{color: "violet"}}>{`fake: ${fakeCount}`}</Text>}
				<Text style={{opacity: .6}}>{`seeded word counts: ${result.reshapeWordCounts.join(", ")}`}</Text>
				<Text style={{opacity: .6}}>{`tokens: ${result.outputTokens}`}</Text>
			</Row>

			{views.map(view=>{
				const slot = result.slots.find(a=>a.slotId == view.slotId)!;
				// the ORIGINAL text is only shown with labels on: for a real bullet it is the user's own
				// dream content, which is exactly what must not be visible while judging the pool
				return (
					<Column key={view.slotId} style={{padding: 6, borderRadius: 4, background: "rgba(255,255,255,.05)"}}>
						<Row center style={{gap: 8, flexWrap: "wrap"}}>
							<Text style={{fontSize: 11, opacity: .5}}>{view.slotId.substring(0, 8)}</Text>
							{showLabels &&
								<Text style={{fontSize: 11, color: slot.sourceKind == "real" ? "lightgreen" : "violet"}}>{slot.sourceKind}</Text>}
							<Text style={{fontSize: 11, color: "orange"}}>{slot.reshapeWords.length > 0 ? `seeds: ${slot.reshapeWords.join(" + ")}` : "unseeded"}</Text>
							{showLabels && slot.sourceKind == "real" &&
								<Text style={{fontSize: 11, color: slot.retentionOk ? "lightgreen" : "red"}}>{slot.retentionOk ? "retained" : "LOST EVENT"}</Text>}
						</Row>
						<Text style={{marginTop: 2}}>{view.text}</Text>
					</Column>
				);
			})}

			{result.dropped.length > 0 &&
				<Column mt={6} style={{gap: 2}}>
					<Text style={{fontWeight: "bold", color: "orange"}}>{`Dropped (${result.dropped.length}) -- the round is smaller than requested:`}</Text>
					{result.dropped.map((drop, i)=>
						<Text key={i} style={{fontSize: 11, opacity: .85}}>
							{`${showLabels ? `${drop.sourceKind} ` : ""}${drop.sourceId.substring(0, 12)}: ${drop.reason}`}
						</Text>,
					)}
				</Column>}

			{result.generatedBullets.length > 0 &&
				<Column mt={6} style={{gap: 2}}>
					<Text style={{fontWeight: "bold", opacity: .8}}>{`Generated candidates (${result.generatedBullets.length}), including rejected -- these live only on the round, never in the corpus (§5):`}</Text>
					{result.generatedBullets.map((candidate, i)=>
						<Text key={i} style={{fontSize: 11, opacity: candidate.rejected == null ? .9 : .6}}>
							{`${candidate.rejected == null ? "accepted" : `rejected: ${candidate.rejected}`} | ${candidate.text}`}
						</Text>,
					)}
				</Column>}
		</Column>
	);
});
